import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import Picker from "emoji-picker-react";
import { comment } from "../../functions/post";
import { uploadImages } from "../../functions/uploadImages";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { ClipLoader } from "react-spinners";

const CreateComment = forwardRef(function CreateComment({ user, postId, setComments, setCount }, ref) {
  const [picker, setPicker] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [commentImage, setCommentImage] = useState("");
  const [cursorPosition, setCursorPosition] = useState();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // GIF and Sticker states
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState("");
  const [gifs, setGifs] = useState([]);
  const [selectedGif, setSelectedGif] = useState(null);
  const [selectedSticker, setSelectedSticker] = useState(null);
  const [gifLoading, setGifLoading] = useState(false);
  const gifSearchInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const wasPickerOpenRef = useRef(false);
  
  const textRef = useRef(null);
  const imgInput = useRef(null);

  // Expose focus and scrollIntoView to parent (e.g. when Comment button is clicked)
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textRef.current) {
        textRef.current.focus();
        textRef.current.closest?.(".comment_input_wrap")?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      }
    },
  }));

  useEffect(() => {
    textRef.current.selectionEnd = cursorPosition;
  }, [cursorPosition]);

  // Close other pickers when one is opened
  useEffect(() => {
    if (showGifPicker) {
      setPicker(false);
      setShowStickerPicker(false);
    }
  }, [showGifPicker]);

  useEffect(() => {
    if (showStickerPicker) {
      setPicker(false);
      setShowGifPicker(false);
    }
  }, [showStickerPicker]);

  useEffect(() => {
    if (picker) {
      setShowGifPicker(false);
      setShowStickerPicker(false);
    }
  }, [picker]);

  // On mobile: hide keyboard when emoji picker opens, refocus when it closes.
  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) {
      wasPickerOpenRef.current = picker;
      return;
    }

    if (picker) {
      textRef.current?.blur?.();
    } else if (wasPickerOpenRef.current) {
      textRef.current?.focus?.();
    }

    wasPickerOpenRef.current = picker;
  }, [picker]);

  // Close emoji picker when clicking outside the picker/button.
  useEffect(() => {
    if (!picker) return;

    const handleClickOutside = (event) => {
      const clickedInsidePicker = emojiPickerRef.current?.contains(event.target);
      const clickedEmojiButton = emojiButtonRef.current?.contains(event.target);
      if (!clickedInsidePicker && !clickedEmojiButton) {
        setPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [picker]);

  // Focus the GIF search input when the picker opens (handy on mobile)
  useEffect(() => {
    if (showGifPicker && gifSearchInputRef.current) {
      // slight delay to ensure the element is painted before focusing
      setTimeout(() => gifSearchInputRef.current?.focus(), 50);
    }
  }, [showGifPicker]);

  const handleEmoji = (e, { emoji }) => {
    const ref = textRef.current;
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) {
      ref.focus();
    }
    const caret = typeof ref?.selectionStart === "number" ? ref.selectionStart : text.length;
    const start = text.substring(0, caret);
    const end = text.substring(caret);
    const newText = start + emoji + end;
    setText(newText);
    setCursorPosition(start.length + emoji.length);
  };

    // GIF API integration
  const searchGifs = async (query) => {
    if (!query.trim()) return;
    
    setGifLoading(true);
    setError(""); // Clear any previous errors
    
    try {
      // Using Giphy API with a working API key
      // For production, get a free API key from https://developers.giphy.com/
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=GlVGYHkr3WSBnllca54iNt0yFbjz7L65&q=${encodeURIComponent(query)}&limit=20&rating=g`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Giphy API response:", data);
      
      if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        setGifs(data.data);
      } else {
        console.warn("No GIFs found for query:", query);
        // Fallback to mock GIFs for testing
        const mockGifs = [
          {
            id: 'mock1',
            title: 'Happy',
            images: {
              fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
              fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
            }
          },
          {
            id: 'mock2',
            title: 'Laugh',
            images: {
              fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
              fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
            }
          },
          {
            id: 'mock3',
            title: 'Love',
            images: {
              fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
              fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
            }
          }
        ];
        setGifs(mockGifs);
        setError("No GIFs found for your search. Showing demo GIFs instead.");
      }
    } catch (error) {
      console.error("Error fetching GIFs:", error);
      // Fallback to mock GIFs for testing
      const mockGifs = [
        {
          id: 'mock1',
          title: 'Happy',
          images: {
            fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
            fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
          }
        },
        {
          id: 'mock2',
          title: 'Laugh',
          images: {
            fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
            fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
          }
        },
        {
          id: 'mock3',
          title: 'Love',
          images: {
            fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
            fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
          }
        },
        {
          id: 'mock4',
          title: 'Wow',
          images: {
            fixed_height: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' },
            fixed_height_small: { url: 'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif' }
          }
        }
      ];
      setGifs(mockGifs);
      setError("GIF search failed. Using demo GIFs instead. Please try again later.");
    } finally {
      setGifLoading(false);
    }
  };

  const handleGifSearch = (e) => {
    const query = e.target.value;
    setGifSearch(query);
    
    // Clear any previous errors when starting a new search
    if (query.trim() !== gifSearch.trim()) {
      setError("");
    }
    
    // Debounce search
    clearTimeout(gifSearchTimeout.current);
    gifSearchTimeout.current = setTimeout(() => {
      if (query.trim()) {
        searchGifs(query);
      } else {
        setGifs([]);
      }
    }, 500);
  };

  const retryGifSearch = () => {
    if (gifSearch.trim()) {
      setError("");
      searchGifs(gifSearch);
    }
  };

  const gifSearchTimeout = useRef(null);

  const handleGifSelect = (gif) => {
    console.log("GIF selected:", gif);
    setSelectedGif(gif);
    setShowGifPicker(false);
    setGifSearch("");
    setGifs([]);
  };

  const handleStickerSelect = (sticker) => {
    setSelectedSticker(sticker);
    setShowStickerPicker(false);
  };

  const removeGif = () => {
    setSelectedGif(null);
  };

  const removeSticker = () => {
    setSelectedSticker(null);
  };

  // Sticker categories
  const stickerCategories = [
    { id: "emoji", name: "Emoji", stickers: ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳"] },
    { id: "animals", name: "Animals", stickers: ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🐤", "🦆", "🦅", "🦉", "🦇", "🐺", "🐗", "🐴", "🦄", "🐝", "🐛", "🦋", "🐌"] },
    { id: "food", name: "Food", stickers: ["🍎", "🍐", "🍊", "🍋", "🍌", "🍉", "🍇", "🍓", "🍈", "🍒", "🍑", "🥭", "🍍", "🥥", "🥝", "🍅", "🥑", "🍆", "🥔", "🥕", "🌽", "🌶️", "🥒", "🥬", "🥦", "🧄", "🧅", "🥜", "🌰", "🥑", "🍞"] },
    { id: "activities", name: "Activities", stickers: ["⚽", "🏀", "🏈", "⚾", "🥎", "🎾", "🏐", "🏉", "🥏", "🎱", "🪀", "🏓", "🏸", "🏒", "🏑", "🥍", "🏏", "🥅", "⛳", "🪁", "🏹", "🎣", "🤿", "🥊", "🥋", "🎽", "🛹", "🛷", "⛸️", "🥌", "🎿"] }
  ];

  const handleImage = (e) => {
    let file = e.target.files[0];
    
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
    if (!allowedTypes.includes(file.type)) {
      setError(`${file.name} format is not supported. Please use JPG, PNG, WEBP, GIF, or AVIF.`);
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 1024 * 1024 * 5) {
      setError(`${file.name} is too large. Maximum size allowed is 5MB.`);
      return;
    }

    setError(""); // Clear any previous errors

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      setCommentImage(event.target.result);
      setImagePreview({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2), // Size in MB
        type: file.type
      });
    };
  };

  const removeImage = () => {
    setCommentImage("");
    setImagePreview(null);
    if (imgInput.current) {
      imgInput.current.value = '';
    }
  };

  const handleComment = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      await submitComment();
    }
  };

  const handleSubmitClick = async () => {
    await submitComment();
  };

  const submitComment = async () => {
    // Don't submit if all content is empty
    if (!text.trim() && !commentImage && !selectedGif && !selectedSticker) {
      console.log("No content to submit");
      return;
    }

    // Prevent multiple submissions
    if (loading) {
      console.log("Already submitting comment");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let imageUrl = "";
      let gifUrl = "";
      let stickerUrl = "";
      
      if (commentImage) {
        const img = dataURItoBlob(commentImage, `comment_image_${Date.now()}.jpg`);
        const path = `${user.username}/post_images/${postId}`;
        let formData = new FormData();
        formData.append("path", path);
        formData.append("file", img);
        
        const imgComment = await uploadImages(formData, path, user.token);
        imageUrl = imgComment[0]?.url || "";
      }

      // Add GIF URL if selected
      if (selectedGif) {
        gifUrl = selectedGif.images.fixed_height.url;
        console.log("Selected GIF URL:", gifUrl);
        console.log("Full selected GIF object:", selectedGif);
      }

      // Add sticker URL if selected
      if (selectedSticker) {
        stickerUrl = selectedSticker;
        console.log("Selected sticker:", stickerUrl);
      }

      // Validate that we have some content to submit
      if (!text.trim() && !imageUrl && !gifUrl && !stickerUrl) {
        setError("Please add some content to your comment");
        setLoading(false);
        return;
      }

      console.log("Submitting comment with:", { text, imageUrl, gifUrl, stickerUrl });
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const commentPromise = comment(postId, text, imageUrl, user.token, gifUrl, stickerUrl);
      const comments = await Promise.race([commentPromise, timeoutPromise]);
      
      console.log("Comment submission response:", comments);
      
      if (typeof comments === "string") {
        console.error("Comment submission failed:", comments);
        setError(comments);
      } else if (Array.isArray(comments)) {
        console.log("Updated comments from server:", comments);
        // Show the newly added comment in the list immediately.
        const newComment = comments[comments.length - 1];
        if (newComment) {
          const withTime = { ...newComment, commentAt: newComment.commentAt || new Date().toISOString() };
          setComments(prev => {
            if (!Array.isArray(prev)) return comments;
            const filtered = prev.filter(c => (c._id || c.id || c.tempId) !== (withTime._id || withTime.id || withTime.tempId));
            return [withTime, ...filtered];
          });
        } else {
          setComments(comments);
        }
        // Increment count immutably
        setCount(prev => (typeof prev === 'number' ? prev + 1 : 1));
        setText("");
        removeImage();
        removeGif();
        removeSticker();
        setPicker(false); // Close emoji picker after successful submission
        setShowGifPicker(false); // Close GIF picker if open
        setShowStickerPicker(false); // Close sticker picker if open
        console.log("Comment submitted successfully!");
      } else if (comments && typeof comments === 'object') {
        // In case the API returns a single comment object – show it in the list immediately
        const newComment = { ...comments, commentAt: comments.commentAt || new Date().toISOString() };
        setComments(prev => (Array.isArray(prev) ? [newComment, ...prev] : [newComment]));
        setCount(prev => (typeof prev === 'number' ? prev + 1 : 1));
        setText("");
        removeImage();
        removeGif();
        removeSticker();
        setPicker(false); // Close emoji picker after successful submission
        setShowGifPicker(false); // Close GIF picker if open
        setShowStickerPicker(false); // Close sticker picker if open
      } else {
        console.error("Invalid response format:", comments);
        setError("Invalid response from server");
      }
    } catch (error) {
      console.error("Comment submission error:", error);
      if (error.message === 'Request timeout') {
        setError("Request timed out. Please try again.");
      } else {
        setError("Failed to post comment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create_comment_wrap">
      <div className="create_comment">
        <img src={user?.picture} alt="" />
        <div className="comment_input_wrap">
          {picker && (
            <div className="comment_emoji_picker" ref={emojiPickerRef}>
              <Picker onEmojiClick={handleEmoji} />
            </div>
          )}
          
          {/* GIF Picker */}
          {showGifPicker && (
            <div className="comment_gif_picker">
              <div className="gif_picker_header">
                <input
                  type="text"
                  placeholder="Search GIFs..."
                  value={gifSearch}
                  onChange={handleGifSearch}
                  className="gif_search_input"
                  ref={gifSearchInputRef}
                />
                <button 
                  className="close_picker_btn"
                  onClick={() => setShowGifPicker(false)}
                >
                  ✕
                </button>
              </div>
              <div className="gif_results">
                {error && (
                  <div className="gif_error">
                    <span>{error}</span>
                    <div className="error_actions">
                      <button 
                        className="retry_btn"
                        onClick={retryGifSearch}
                        title="Retry search"
                      >
                        🔄 Retry
                      </button>
                      <button 
                        className="error_close"
                        onClick={() => setError("")}
                        title="Dismiss error"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                {gifLoading ? (
                  <div className="gif_loading">
                    <ClipLoader size={20} color="#1876f2" loading={true} />
                    <span>Loading GIFs...</span>
                  </div>
                ) : gifs.length > 0 ? (
                  <div className="gif_grid">
                    {gifs.map((gif) => (
                      <img
                        key={gif.id}
                        src={gif.images.fixed_height_small.url}
                        alt={gif.title}
                        onClick={() => handleGifSelect(gif)}
                        className="gif_thumbnail"
                      />
                    ))}
                  </div>
                ) : gifSearch && !gifLoading ? (
                  <div className="no_gifs">No GIFs found</div>
                ) : (
                  <div className="gif_placeholder">Search for GIFs above</div>
                )}
              </div>
            </div>
          )}
          
          {/* Sticker Picker */}
          {showStickerPicker && (
            <div className="comment_sticker_picker">
              <div className="sticker_picker_header">
                <span>Choose a sticker</span>
                <button 
                  className="close_picker_btn"
                  onClick={() => setShowStickerPicker(false)}
                >
                  ✕
                </button>
              </div>
              <div className="sticker_categories">
                {stickerCategories.map((category) => (
                  <div key={category.id} className="sticker_category">
                    <h4>{category.name}</h4>
                    <div className="sticker_grid">
                      {category.stickers.map((sticker, index) => (
                        <span
                          key={index}
                          className="sticker_item"
                          onClick={() => handleStickerSelect(sticker)}
                        >
                          {sticker}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Hidden file input */}
          <input
            type="file"
            hidden
            ref={imgInput}
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            onChange={handleImage}
          />
          
          {/* Error display */}
          {error && (
            <div className="postError comment_error">
              <div className="postError_error">{error}</div>
              <button className="blue_btn" onClick={() => setError("")}>
                Try again
              </button>
            </div>
          )}
          
          {/* Comment input */}
          <input
            type="text"
            ref={textRef}
            value={text}
            placeholder={commentImage || selectedGif || selectedSticker ? "Add a caption.." : "Write a comment."}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleComment}
            disabled={loading}
          />
          
          {/* Action buttons */}
          <div className="comment_actions_row">
            {/* Loading spinner */}
            {loading && (
              <div className="comment_circle">
                <ClipLoader size={20} color="#1876f2" loading={true} />
              </div>
            )}
            
            {/* Submit button - only show if there's content */}
            {(text.trim() || commentImage || selectedGif || selectedSticker) && !loading && (
              <div 
                className="comment_circle_icon hover2 submit_comment"
                onClick={handleSubmitClick}
                title="Post comment"
              >
                <span className="send_icon">➤</span>
              </div>
            )}
            
            {/* Emoji picker */}
            <div
              className={`comment_circle_icon hover2 emoji_btn ${picker ? 'active' : ''}`}
              ref={emojiButtonRef}
              onClick={() => setPicker((prev) => !prev)}
              title="Add emoji"
            >
              <i className="emoji_icon"></i>
            </div>
            
            {/* Camera icon for image upload */}
            <div
              className={`comment_circle_icon hover2 camera_btn ${commentImage ? 'has_image' : ''}`}
              onClick={() => imgInput.current.click()}
              title="Add photo"
            >
              <i className="camera_icon"></i>
            </div>
            
            {/* GIF button */}
            <div 
              className={`comment_circle_icon hover2 gif_btn ${showGifPicker ? 'active' : ''} ${selectedGif ? 'has_gif' : ''}`}
              onClick={() => setShowGifPicker((prev) => !prev)}
              title="Add GIF"
            >
              <i className="gif_icon"></i>
            </div>
            
            {/* Sticker button */}
            <div 
              className={`comment_circle_icon hover2 sticker_btn ${showStickerPicker ? 'active' : ''} ${selectedSticker ? 'has_sticker' : ''}`}
              onClick={() => setShowStickerPicker((prev) => !prev)}
              title="Add sticker"
            >
              <i className="sticker_icon"></i>
            </div>
          </div>
        </div>
      </div>
      
      {/* Enhanced image preview with send button */}
      {commentImage && (
        <div className="comment_img_preview enhanced">
          <div className="preview_header">
            <span className="preview_label notranslate">📷 Image attached</span>
            {imagePreview && (
              <span className="preview_info">
                {imagePreview.name} ({imagePreview.size} MB)
              </span>
            )}
          </div>
          <div className="preview_image_container">
            <img src={commentImage} alt="Comment preview" />
            
            {/* Action buttons for image */}
            <div className="image_action_buttons">
              {/* image_send_button removed per request; use main submit instead */}
              
              {/* Loading spinner for image */}
              {loading && (
                <div className="image_loading">
                  <ClipLoader size={20} color="#fff" loading={true} />
                  <span>Posting...</span>
                </div>
              )}
              
              {/* Remove button */}
              <div
                className="small_white_circle"
                onClick={removeImage}
                title="Remove image"
              >
                <i className="exit_icon"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GIF preview */}
      {selectedGif && (
        <div className="comment_gif_preview">
          <div className="preview_header">
            <span className="preview_label">🎬 GIF attached</span>
          </div>
          <div className="preview_gif_container">
            <img src={selectedGif.images.fixed_height.url} alt="GIF preview" />
            
            {/* Action buttons for GIF */}
            <div className="gif_action_buttons">
              {/* Send button */}
              {!loading && (
                <button 
                  className="gif_send_button"
                  onClick={handleSubmitClick}
                  title="Post comment with GIF"
                >
                  <span className="send_icon">➤</span>
                  <span className="send_text">Post</span>
                </button>
              )}
              
              {/* Loading spinner for GIF */}
              {loading && (
                <div className="gif_loading">
                  <ClipLoader size={20} color="#fff" loading={true} />
                  <span>Posting...</span>
                </div>
              )}
              
              {/* Remove button */}
              <div
                className="small_white_circle"
                onClick={removeGif}
                title="Remove GIF"
              >
                <i className="exit_icon"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticker preview */}
      {selectedSticker && (
        <div className="comment_sticker_preview">
          <div className="preview_header">
            <span className="preview_label notranslate">🎯 Sticker attached</span>
          </div>
          <div className="preview_sticker_container">
            <span className="sticker_preview">{selectedSticker}</span>
            
            {/* Action buttons for sticker */}
            <div className="sticker_action_buttons">
              {/* Send button */}
              {!loading && (
                <button 
                  className="sticker_send_button"
                  onClick={handleSubmitClick}
                  title="Post comment with sticker"
                >
                  <span className="send_icon">➤</span>
                  <span className="send_text">Post</span>
                </button>
              )}
              
              {/* Loading spinner for sticker */}
              {loading && (
                <div className="sticker_loading">
                  <ClipLoader size={20} color="#fff" loading={true} />
                  <span>Posting...</span>
                </div>
              )}
              
              {/* Remove button */}
              <div
                className="small_white_circle"
                onClick={removeSticker}
                title="Remove sticker"
              >
                <i className="exit_icon"></i>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CreateComment;
